<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfig } from 'dashboard/composables/useConfig';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { hostURL } = useConfig();

const INLINE_TARGET_ID = 'chatwoot-inline-chat';

const activeTabIndex = ref(0);

const modes = computed(() => [
  {
    key: 'STANDARD',
    label: t('INBOX_MGMT.WIDGET_INSTALLATION.MODES.STANDARD.LABEL'),
    description: t('INBOX_MGMT.WIDGET_INSTALLATION.MODES.STANDARD.DESCRIPTION'),
  },
  {
    key: 'INLINE',
    label: t('INBOX_MGMT.WIDGET_INSTALLATION.MODES.INLINE.LABEL'),
    description: t('INBOX_MGMT.WIDGET_INSTALLATION.MODES.INLINE.DESCRIPTION'),
  },
  {
    key: 'FULLSCREEN',
    label: t('INBOX_MGMT.WIDGET_INSTALLATION.MODES.FULLSCREEN.LABEL'),
    description: t(
      'INBOX_MGMT.WIDGET_INSTALLATION.MODES.FULLSCREEN.DESCRIPTION'
    ),
  },
  {
    key: 'LINK',
    label: t('INBOX_MGMT.WIDGET_INSTALLATION.MODES.LINK.LABEL'),
    description: t('INBOX_MGMT.WIDGET_INSTALLATION.MODES.LINK.DESCRIPTION'),
  },
]);

const activeMode = computed(() => modes.value[activeTabIndex.value]);
const isLinkMode = computed(() => activeMode.value.key === 'LINK');
const tabs = computed(() => modes.value.map(({ label }) => ({ label })));

const chatLink = computed(() => `${hostURL}/chat/${props.inbox.website_token}`);

// The generated script opens with "\n    <script>", so widget settings are spliced in
// right after it, the same way the widget preview builds its snippet.
const buildScript = (settings = null) => {
  const script = props.inbox.web_widget_script || '';
  if (!settings) return script;

  const declaration = `\n      window.chatwootSettings = ${JSON.stringify(settings)};`;
  return script.substring(0, 13) + declaration + script.substring(13);
};

const snippet = computed(() => {
  if (activeMode.value.key === 'INLINE') {
    // Indented to match the generated script tag, so the code block dedents both together.
    const container = `\n    <div id="${INLINE_TARGET_ID}" style="height: 600px;"></div>`;
    return (
      container +
      buildScript({
        displayMode: 'inline',
        inlineTarget: `#${INLINE_TARGET_ID}`,
      })
    );
  }
  if (activeMode.value.key === 'FULLSCREEN') {
    return buildScript({ displayMode: 'fullscreen' });
  }
  return buildScript();
});

const handleTabChange = tab => {
  activeTabIndex.value = tabs.value.findIndex(item => item.label === tab.label);
};

const openChatLink = () => {
  window.open(chatLink.value, '_blank', 'noopener');
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <span class="text-heading-2 text-n-slate-12">
        {{ $t('INBOX_MGMT.WIDGET_INSTALLATION.TITLE') }}
      </span>
      <span class="text-body-main text-n-slate-11">
        {{ $t('INBOX_MGMT.WIDGET_INSTALLATION.DESCRIPTION') }}
      </span>
    </div>

    <TabBar
      :tabs="tabs"
      :initial-active-tab="activeTabIndex"
      @tab-changed="handleTabChange"
    />

    <p class="text-body-main text-n-slate-11 mb-0">
      {{ activeMode.description }}
    </p>

    <div v-if="isLinkMode" class="flex flex-col gap-3">
      <woot-code :script="chatLink" lang="html" />
      <div class="flex">
        <NextButton
          faded
          slate
          sm
          icon="i-lucide-external-link"
          :label="$t('INBOX_MGMT.WIDGET_INSTALLATION.MODES.LINK.OPEN')"
          @click="openChatLink"
        />
      </div>
    </div>
    <woot-code v-else :script="snippet" lang="html" />
  </div>
</template>
